<script setup>
import { computed } from 'vue';

const props = defineProps({  
  data: {
    type: Array,
    required: true,
  }, 
  chartType: {
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value),
  },  
  comparisonTarget: {
    type: String,
    required: true,
    validator: (value) => ['overall', 'male', 'female'].includes(value),
  },
  maxScale: {
    type: Number,
    default: 100,
  },
  chartHeight: {
    type: String,
    default: '200px',
  },
  chartTitle: {
    type: String,
    default: '그룹 비교 그래프',
  },
});

// AABBCCDD (DD = 투명도, 80 = 50% 투명)
const LITERACY_TOTAL_SCHOOL_COLOR = '#6484F080'; 
const NUMERACY_TOTAL_SCHOOL_COLOR = '#F8808280'; 
const OUR_SCHOOL_COLOR = '#00A49680';

const fixedGroups = computed(() => {
  const isLiteracy = props.chartType === 'literacy';

  const totalSchoolBaseColor = isLiteracy ? LITERACY_TOTAL_SCHOOL_COLOR : NUMERACY_TOTAL_SCHOOL_COLOR;
  const ourSchoolBaseColor = OUR_SCHOOL_COLOR;

  if (props.comparisonTarget === 'overall') { 
    return [
      { key: 'totalSchool', label: '전체학교', color: totalSchoolBaseColor },
      { key: 'ourSchool', label: '우리학교', color: ourSchoolBaseColor },
    ];
  } else if (props.comparisonTarget === 'male') { 
    return [
      { key: 'totalSchoolMale', label: '전체학교 남', color: totalSchoolBaseColor },
      { key: 'ourSchoolMale', label: '우리학교 남', color: ourSchoolBaseColor },
    ];
  } else { 
    return [
      { key: 'totalSchoolFemale', label: '전체학교 여', color: totalSchoolBaseColor },
      { key: 'ourSchoolFemale', label: '우리학교 여', color: ourSchoolBaseColor },
    ];
  }
});

const getBarHeight = (value) => {
  const numericValue = Number(value); // 숫자 형변환 
  // value가 null, undefined, 빈 문자열이거나 NaN인 경우 0% 반환
  if (value === null || value === undefined || value === '' || isNaN(numericValue)) {
    return '0%';
  }
  
  if (props.maxScale === 0) return '0%';
  const percentage = (numericValue / props.maxScale) * 100;
  return Math.max(0, Math.min(100, percentage)) + '%';
};

const getDisplayValue = (value) => {
  // value가 null, undefined, 빈 문자열이거나 NaN인 경우 'N' 반환
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return 'N';
  }
  return value; // 유효한 숫자면 그대로 반환
};

const legendItems = computed(() => {
  return fixedGroups.value;
});

// 10% 단위 그리드 라인 위치 (10% ~ 90%)
const gridLines = computed(() => {
  const lines = [];
  for (let i = 1; i < 10; i++) { 
    lines.push(i * 10);
  }
  return lines;
});
</script>

<template>
  <div class="grouped-chart-wrapper" :class="props.comparisonTarget">   
    <div class="grouped-chart-container">
      <!-- <div class="chart-y-axis">        
        <div class="y-axis-label" v-for="n in 5" :key="n" :style="{ bottom: ((n - 1) * 25) + '%' }">
          {{ Math.round(props.maxScale / 4 * (n - 1)) }}%
        </div>
      </div> -->
      <div class="chart-groups-wrap" :class="props.chartType">
        <div class="chart-grid-lines" :style="{ height: chartHeight }">
          <div
            v-for="pos in gridLines"
            :key="pos"
            class="grid-line"
            :style="{ bottom: pos + '%' }"
          ></div>
        </div>
        <div v-for="(item, itemIndex) in props.data" :key="itemIndex" class="bar-group"> 
            <n-flex :wrap="false" :size="20" :style="{ height: chartHeight }">         
                <div v-for="(group, groupIndex) in fixedGroups" :key="groupIndex" class="bar-item"> 
                    <div class="bar-value">
                    <span class="score-value">
                        {{ getDisplayValue(item[group.key]) }}{{ getDisplayValue(item[group.key]) === 'N%' ? '' : '%' }}
                    </span>     
                    <span v-if="!group.key.startsWith('totalSchool') && item[group.key + 'Count'] !== undefined" class="count-value">
                        ({{ getDisplayValue(item[group.key + 'Count']) }}{{ getDisplayValue(item[group.key + 'Count']) === 'N명' ? '' : '명' }})
                    </span>            
                    </div>
                    <div
                    class="bar"
                    :style="{ height: getBarHeight(item[group.key]), backgroundColor: group.color }"
                    ></div>
                </div>
            </n-flex>
            <div class="group-label">{{ item.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.grouped-chart-container {
  display: flex;
  align-items: flex-end; 
  width: 100%;
  position: relative;

  .chart-groups-wrap {
    display: flex;
    flex: 1;
    justify-content: space-around;
    height: 100%; 

    .bar-group {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        height: 100%;     
        .n-flex {
            height: calc(100% - 42px);
            gap: 20px;
            border-bottom: 2px solid;
            padding: 0 10px;
            .bar-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-end;
                width: 8px;
                margin: 0 4px;
                text-align: center;
                position: relative;
                height: 100%; 
                .bar-value {
                    font-size: 13px;
                    padding-bottom: 10px ;
                    line-height: 1.3;
                }
                .bar {
                    width: 100%; 
                    border-radius: 4px 4px 0 0;
                    position: relative;
                    min-height: 3px; 
                }
            }
        }
    }
    .group-label {
        font-size: 16px;
        width: 60px;
        height: 32px;
        text-align: center;
        background-color: #ccc; // default
        border-radius: 5px;
        margin-top: 10px;
        line-height: 32px;
    }
    &.literacy {
      @for $i from 2 through 5 {
          .bar-group:nth-child(#{$i}) .group-label {
            $opacity: 0; 
            @if $i == 2 {
                $opacity: 0.05;
            } @else if $i == 3 {
                $opacity: 0.1;
            } @else if $i == 4 {
                $opacity: 0.2;
            } @else if $i == 5 {
                $opacity: 0.3;
            }
            background-color: rgba(var(--result-light-blue-rgb), $opacity);
        }
      }
    }
     &.numeracy {
      @for $i from 2 through 5 {
          .bar-group:nth-child(#{$i}) .group-label {
            $opacity: 0; 
            @if $i == 2 {
                $opacity: 0.05;
            } @else if $i == 3 {
                $opacity: 0.1;
            } @else if $i == 4 {
                $opacity: 0.2;
            } @else if $i == 5 {
                $opacity: 0.3;
            }
            background-color: rgba(var(--result-light-red-rgb), $opacity);
        }
      }
    }
  }
}

// 전체 그래프
.overall .grouped-chart-container .chart-groups-wrap {
    .bar-group 
    .n-flex {
        .bar-item {
            width: 16px;
            .bar-value {
                font-size: 14px;
            }
        }
    }
    .group-label {
        width: 80px;
        margin-top: 20px;
    }
}

@mixin gender-after-badge {
    border: 1px solid #ccc; // default
    width: calc(100% - 8px);
    display: inline-block;
    margin: 0 4px;
    border-radius: 5px;
    text-align: center;
    margin-top: 8px;
    height: 28px;
    line-height: 28px;
    .literacy & { 
        border-color: #4362CC;
        color: #4362CC; 
    }
    .numeracy & {
        border-color: #CE4E56;
        color: #CE4E56; 
    }
}
.male {
    position: relative;
    &::after {
        @include gender-after-badge; 
        content: '남학생';
    }
}
.female {
    position: relative;
    &::after {
        @include gender-after-badge;
        content: '여학생';
    }
}

// 그리드
.chart-grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  pointer-events: none; 
  z-index: -1; 
}

.grid-line {
  position: absolute;
  left: 0;
  width: 100%;
  border-bottom: 2px dashed #E9E9E9; 
}
</style>